/**
 * Admin Conversations inbox (#277, WS-F).
 *
 * Staff-facing inbox bound to the already-shipped server router
 * `buildAdminMessagingV2Router` mounted at /api/v2/admin/messaging
 * (apps/server/src/routes/admin-messaging.ts). That router resolves the
 * caller's REAL tenant via resolveStaffTenant ($queryRaw) and scopes the
 * conversation list by the customer relation, so this page never has to
 * pass a tenant — isolation is enforced server-side.
 *
 * Contract (read from the server router + core messaging routes):
 *   GET    /conversations[?status=]      → { conversations: RawListRow[] }
 *   GET    /conversations/:id            → { conversation, messages: AdminMessage[] }
 *   POST   /conversations/:id/messages   → { message }            (staff reply)
 *   POST   /conversations/:id/assign     → { conversation }       (assign-to-me)
 *   POST   /conversations/:id/escalate   → { conversation }
 *   POST   /conversations/:id/de-escalate→ { conversation }
 *
 * The list endpoint returns RAW Prisma rows (latest message in `messages[0]`,
 * count in `_count.messages`, nested `customer`/`assignedStaff`); the detail
 * endpoint returns the core view shape (messages carry `authorType`). Both are
 * normalized defensively below so the UI is robust to either shape.
 *
 * Mirrors the customer-side thread UX (apps/customer-app/src/pages/Messaging.tsx):
 * message bubbles, AI-vs-staff authorship labels, optimistic send, 15s polling.
 * Auth/tenant headers are attached by the shared `api.*` wrapper via
 * adminMessagingApi; the route is already RouteGuard-wrapped in App.tsx, so no
 * extra auth shell is needed here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import { EmptyState, PageHeader, Spinner } from '@ferro/ui';
import { adminMessagingApi } from '../lib/api.js';

type ConversationsProps = { config: ClientConfig };

// ────────────────────────────────────────────────────────────────────────────
// View types — normalized from the two server shapes (raw list row vs. core
// detail view). All fields optional on the wire; the parsers below coerce to
// these stable shapes so the JSX never reaches into `any`.
// ────────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 15_000;
/** Mirrors the core send-message ceiling (DEFAULT_MAX_MESSAGE_LENGTH). */
const MAX_MESSAGE_LENGTH = 2_000;

type ConversationStatus = 'active' | 'closed' | 'escalated';

interface PartyInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
  phone?: string | null;
}

interface InboxConversation {
  id: string;
  channel: string;
  status: ConversationStatus | string;
  phoneNumber: string | null;
  lastMessageAt: string | null;
  createdAt: string | null;
  messageCount: number;
  assignedStaffId: string | null;
  customer: PartyInfo | null;
  assignedStaff: PartyInfo | null;
  lastMessagePreview: string | null;
}

interface ThreadMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string | null;
  /** Server-derived authorship: 'customer' | 'staff' | 'ai' | 'system'. */
  authorType?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Defensive parsers — the list route returns raw Prisma rows, the detail route
// returns the core view shape. Read-only field plucking; never trusts `any`.
// ────────────────────────────────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function parseParty(value: unknown): PartyInfo | null {
  const r = asRecord(value);
  const id = asString(r.id);
  if (!id) return null;
  return {
    id,
    firstName: asString(r.firstName),
    lastName: asString(r.lastName),
    email: asString(r.email),
    phone: asString(r.phone),
  };
}

function parseConversation(value: unknown): InboxConversation | null {
  const r = asRecord(value);
  const id = asString(r.id);
  if (!id) return null;

  // Latest-message preview: the raw list row carries `messages: [latest]`
  // (take 1, desc) and `_count.messages`; the core view carries `lastMessage`
  // + `messageCount`. Support both.
  const messagesArr = Array.isArray(r.messages) ? r.messages : [];
  const firstMessage = asRecord(messagesArr[0]);
  const lastMessageObj = asRecord(r.lastMessage);
  const lastMessagePreview =
    asString(firstMessage.content) ?? asString(lastMessageObj.content);

  const countSource = asRecord(r._count);
  const messageCount =
    typeof r.messageCount === 'number'
      ? r.messageCount
      : typeof countSource.messages === 'number'
        ? (countSource.messages as number)
        : 0;

  return {
    id,
    channel: asString(r.channel) ?? 'web_chat',
    status: (asString(r.status) as ConversationStatus) ?? 'active',
    phoneNumber: asString(r.phoneNumber),
    lastMessageAt: asString(r.lastMessageAt),
    createdAt: asString(r.createdAt),
    messageCount,
    assignedStaffId: asString(r.assignedStaffId),
    customer: parseParty(r.customer),
    assignedStaff: parseParty(r.assignedStaff),
    lastMessagePreview,
  };
}

function parseMessage(value: unknown): ThreadMessage | null {
  const r = asRecord(value);
  const id = asString(r.id);
  if (!id) return null;
  return {
    id,
    role: asString(r.role) ?? 'assistant',
    content: asString(r.content) ?? '',
    createdAt: asString(r.createdAt),
    authorType: asString(r.authorType) ?? undefined,
  };
}

function parseConversationList(data: unknown): InboxConversation[] {
  const r = asRecord(data);
  const arr = Array.isArray(r.conversations)
    ? r.conversations
    : Array.isArray(data)
      ? data
      : [];
  return arr.map(parseConversation).filter((c): c is InboxConversation => c !== null);
}

/**
 * Woofers Phase 2 — whether a reply on a text thread can go out as a text.
 * `null` when the server did not say (an in-app thread, or a server that
 * predates the field during the deploy window): the reply box then stays
 * off with the Phase 1 note, never a box whose reply nobody receives.
 */
type SmsReplyView = { canReply: true } | { canReply: false; message: string };

function parseSmsReply(data: unknown): SmsReplyView | null {
  const conv = asRecord(asRecord(data).conversation);
  const raw = asRecord(conv.smsReply);
  if (raw.canReply === true) return { canReply: true };
  if (raw.canReply === false && typeof raw.message === 'string') return { canReply: false, message: raw.message };
  return null;
}

function parseThread(data: unknown): ThreadMessage[] {
  const r = asRecord(data);
  const arr = Array.isArray(r.messages) ? r.messages : [];
  return arr.map(parseMessage).filter((m): m is ThreadMessage => m !== null);
}

// ────────────────────────────────────────────────────────────────────────────
// Formatting + labels
// ────────────────────────────────────────────────────────────────────────────

function partyName(p: PartyInfo | null, fallback: string): string {
  if (!p) return fallback;
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return name || p.email || p.phone || fallback;
}

function customerLabel(c: InboxConversation): string {
  return partyName(c.customer, c.phoneNumber ?? 'Unknown customer');
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatListTimestamp(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function statusBadgeStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '2px 8px',
    borderRadius: 99,
    whiteSpace: 'nowrap',
  };
  if (status === 'escalated') {
    return { ...base, background: 'var(--ferro-danger, #c0392b)', color: '#fff' };
  }
  if (status === 'closed') {
    return { ...base, background: 'var(--ferro-surface, #e7e2dd)', color: 'inherit', opacity: 0.7 };
  }
  return { ...base, background: 'var(--brand-primary, #b87333)', color: '#fff' };
}

/** Customer messages render right-aligned (inbound); staff/AI left-aligned. */
function isCustomerMessage(msg: ThreadMessage): boolean {
  return msg.authorType === 'customer' || msg.role === 'customer' || msg.role === 'CUSTOMER';
}

function senderLabel(msg: ThreadMessage): string {
  if (isCustomerMessage(msg)) return 'Customer';
  if (msg.authorType === 'ai') return 'AI Assistant';
  if (msg.authorType === 'system' || msg.role === 'system') return 'System';
  return 'Staff';
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: ThreadMessage }): JSX.Element {
  const inbound = isCustomerMessage(msg);
  const label = senderLabel(msg);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: inbound ? 'flex-start' : 'flex-end',
        marginBottom: 8,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          opacity: 0.6,
          margin: '0 4px 2px',
        }}
      >
        {label}
      </span>
      <div
        style={{
          maxWidth: '78%',
          padding: '10px 14px',
          borderRadius: inbound ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
          background: inbound
            ? 'var(--ferro-surface, #f3ede8)'
            : 'var(--brand-primary, #b87333)',
          color: inbound ? 'inherit' : '#fff',
          fontSize: '15px',
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
        role="article"
        aria-label={`${label}: ${msg.content}`}
      >
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
        <time
          style={{
            display: 'block',
            fontSize: 11,
            marginTop: 4,
            opacity: 0.7,
            textAlign: inbound ? 'left' : 'right',
          }}
          dateTime={msg.createdAt ?? undefined}
        >
          {formatTime(msg.createdAt)}
        </time>
      </div>
    </div>
  );
}

function ConversationRow({
  conv,
  active,
  onSelect,
}: {
  conv: InboxConversation;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%',
        textAlign: 'left',
        padding: '12px 14px',
        border: 'none',
        borderBottom: '1px solid var(--ferro-border)',
        background: active ? 'var(--ferro-surface, #f3ede8)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customerLabel(conv)}
        </span>
        <span style={{ fontSize: 11, opacity: 0.6, flexShrink: 0 }}>
          {formatListTimestamp(conv.lastMessageAt ?? conv.createdAt)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={statusBadgeStyle(conv.status)}>{conv.status}</span>
        <span
          style={{
            fontSize: 13,
            opacity: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {conv.lastMessagePreview ?? `${conv.messageCount} message${conv.messageCount === 1 ? '' : 's'}`}
        </span>
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

export function Conversations({ config }: ConversationsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;

  const [conversations, setConversations] = useState<InboxConversation[]>([]);
  const [statusFilter, setStatusFilter] = useState<'' | ConversationStatus>('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [draft, setDraft] = useState('');

  const [listLoading, setListLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);

  const [listError, setListError] = useState<string | null>(null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [smsReply, setSmsReply] = useState<SmsReplyView | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Mirror of `messages` for the background poll (stable callback can't see
  // current thread length otherwise). Same pattern as customer Messaging.
  const messagesRef = useRef<ThreadMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId],
  );

  // ── Load conversation list (also used after mutations) ────────────────────
  const loadConversations = useCallback(
    async (opts?: { keepSelection?: boolean }) => {
      setListError(null);
      const res = await adminMessagingApi.getConversations(
        apiUrl,
        statusFilter || undefined,
      );
      if (res.error) {
        setListError(res.error);
        setListLoading(false);
        return;
      }
      const convs = parseConversationList(res.data);
      setConversations(convs);
      setListLoading(false);
      // Auto-select the first conversation on initial load only — never steal
      // the staffer's current selection out from under them on a refresh.
      if (!opts?.keepSelection) {
        setActiveId((current) => current ?? (convs[0]?.id ?? null));
      }
    },
    [apiUrl, statusFilter],
  );

  useEffect(() => {
    setListLoading(true);
    void loadConversations();
  }, [loadConversations]);

  // ── Load a single thread ──────────────────────────────────────────────────
  const loadThread = useCallback(
    async (conversationId: string, isStale?: () => boolean) => {
      setThreadLoading(true);
      setThreadError(null);
      const res = await adminMessagingApi.getConversation(apiUrl, conversationId);
      // A newer conversation selection superseded this load while it was in
      // flight — drop the response entirely so the later load owns all state
      // (loading / messages / error). Without this guard a fast A→B switch can
      // race two getConversation calls and the LAST to resolve wins, painting
      // customer A's thread under customer B's header. Mirrors the poll effect's
      // `cancelled` flag below.
      if (isStale?.()) return;
      setThreadLoading(false);
      if (res.error) {
        setThreadError(res.error);
        return;
      }
      setMessages(parseThread(res.data));
      setSmsReply(parseSmsReply(res.data));
    },
    [apiUrl],
  );

  useEffect(() => {
    setSmsReply(null);
    if (!activeId) {
      setMessages([]);
      return;
    }
    let stale = false;
    void loadThread(activeId, () => stale);
    return () => {
      stale = true;
    };
  }, [activeId, loadThread]);

  // ── Poll the active thread every 15s (visibility-paused) so customer / AI
  //    replies appear without a manual reload. Background-poll failures never
  //    wipe a visible thread — the next good poll self-heals. ────────────────
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;

    async function poll(): Promise<void> {
      if (document.visibilityState === 'hidden') return;
      const res = await adminMessagingApi.getConversation(apiUrl, activeId as string);
      if (cancelled) return;
      if (res.error) {
        if (messagesRef.current.length === 0) setThreadError(res.error);
        return;
      }
      setThreadError(null);
      setMessages(parseThread(res.data));
      setSmsReply(parseSmsReply(res.data));
    }

    const handle = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [activeId, apiUrl]);

  // Scroll the thread to the bottom on new messages.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send a staff reply ────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || sending || !activeId) return;

    setSending(true);
    setActionError(null);
    setDraft('');

    // Optimistic — staff reply renders immediately on the right.
    const tempMsg: ThreadMessage = {
      id: `temp-${Date.now()}`,
      role: 'assistant',
      content: trimmed,
      createdAt: new Date().toISOString(),
      authorType: 'staff',
    };
    setMessages((prev) => [...prev, tempMsg]);

    const res = await adminMessagingApi.sendMessage(apiUrl, activeId, trimmed);
    if (res.error) {
      // Roll back the optimistic bubble and restore the draft. On a text
      // thread the server sent nothing and saved nothing (it says why), and
      // whether a text can go out may have changed (a STOP just arrived):
      // re-read the thread so the box reflects it.
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      setDraft(trimmed);
      setActionError(res.error);
      if (activeConversation?.channel === 'sms') void loadThread(activeId);
    } else {
      // Reload the canonical thread + refresh the list preview/timestamp.
      await loadThread(activeId);
      void loadConversations({ keepSelection: true });
    }
    setSending(false);
  }, [draft, sending, activeId, apiUrl, loadThread, loadConversations, activeConversation?.channel]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  // ── Assign-to-me / escalate / de-escalate ─────────────────────────────────
  const runConversationAction = useCallback(
    async (
      action: (apiUrl: string, id: string) => Promise<{ error?: string }>,
    ) => {
      if (!activeId || acting) return;
      setActing(true);
      setActionError(null);
      const res = await action(apiUrl, activeId);
      if (res.error) {
        setActionError(res.error);
      } else {
        await loadConversations({ keepSelection: true });
      }
      setActing(false);
    },
    [activeId, acting, apiUrl, loadConversations],
  );

  const handleAssignToMe = useCallback(
    // No staffUserId → the server assigns the authenticated caller (assign-to-me).
    () => runConversationAction((u, id) => adminMessagingApi.assignStaff(u, id)),
    [runConversationAction],
  );
  const handleEscalate = useCallback(
    () => runConversationAction((u, id) => adminMessagingApi.escalate(u, id)),
    [runConversationAction],
  );
  const handleDeEscalate = useCallback(
    () => runConversationAction((u, id) => adminMessagingApi.deEscalate(u, id)),
    [runConversationAction],
  );

  // ── Render ────────────────────────────────────────────────────────────────
  const filters: Array<{ value: '' | ConversationStatus; label: string }> = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'closed', label: 'Closed' },
  ];

  return (
    <section className="ferro-admin-page">
      <PageHeader title="Conversations" subtitle="Customer messaging inbox" />

      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 8, margin: '0 0 12px', flexWrap: 'wrap' }}>
        {filters.map((f) => {
          const selected = statusFilter === f.value;
          return (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              aria-pressed={selected}
              style={{
                padding: '6px 14px',
                borderRadius: 99,
                border: '1px solid var(--brand-primary, #b87333)',
                background: selected ? 'var(--brand-primary, #b87333)' : 'transparent',
                color: selected ? '#fff' : 'var(--brand-primary, #b87333)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 34,
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {actionError && (
        <p role="alert" style={{ color: 'var(--ferro-danger, #c0392b)', fontSize: 14, margin: '0 0 12px' }}>
          {actionError}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'stretch',
          height: 'calc(100dvh - 230px)',
          minHeight: 420,
        }}
      >
        {/* ── Conversation list ── */}
        <aside
          style={{
            width: 320,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--ferro-border)',
            borderRadius: 'var(--ferro-radius)',
            background: 'var(--ferro-surface, #fafaf9)',
            overflow: 'hidden',
          }}
          aria-label="Conversation list"
        >
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {listLoading ? (
              <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                <Spinner size="md" />
              </div>
            ) : listError ? (
              <div role="alert" style={{ padding: 16 }}>
                <EmptyState
                  title="Couldn't load conversations"
                  description={listError}
                  action={{ label: 'Try again', onClick: () => { setListLoading(true); void loadConversations(); } }}
                />
              </div>
            ) : conversations.length === 0 ? (
              <EmptyState
                title="No conversations yet"
                description="Customer messages will appear here as they come in."
              />
            ) : (
              conversations.map((conv) => (
                <ConversationRow
                  key={conv.id}
                  conv={conv}
                  active={conv.id === activeId}
                  onSelect={() => setActiveId(conv.id)}
                />
              ))
            )}
          </div>
        </aside>

        {/* ── Thread panel ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--ferro-border)',
            borderRadius: 'var(--ferro-radius)',
            background: 'var(--ferro-surface, #fafaf9)',
            overflow: 'hidden',
            minWidth: 0,
          }}
          aria-label="Conversation thread"
        >
          {!activeConversation ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <EmptyState
                title="Select a conversation"
                description="Choose a customer thread from the list to read and reply."
              />
            </div>
          ) : (
            <>
              {/* Thread header + actions */}
              <header
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--ferro-border)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {customerLabel(activeConversation)}
                    </h2>
                    <span style={statusBadgeStyle(activeConversation.status)}>
                      {activeConversation.status}
                    </span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.7 }}>
                    {activeConversation.assignedStaff
                      ? `Assigned to ${partyName(activeConversation.assignedStaff, 'staff')}`
                      : 'Unassigned'}
                    {activeConversation.customer?.email ? ` · ${activeConversation.customer.email}` : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost ferro-btn--sm"
                    onClick={() => void handleAssignToMe()}
                    disabled={acting}
                  >
                    Assign to me
                  </button>
                  {activeConversation.status === 'escalated' ? (
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-btn--sm"
                      onClick={() => void handleDeEscalate()}
                      disabled={acting}
                    >
                      De-escalate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-btn--sm"
                      onClick={() => void handleEscalate()}
                      disabled={acting || activeConversation.status === 'closed'}
                    >
                      Escalate
                    </button>
                  )}
                </div>
              </header>

              {/* Messages */}
              <div
                style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 0 }}
                aria-live="polite"
                aria-label="Messages"
              >
                {threadLoading && messages.length === 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <Spinner size="md" />
                  </div>
                ) : threadError ? (
                  <div role="alert">
                    <EmptyState
                      title="Couldn't load messages"
                      description={threadError}
                      action={{ label: 'Try again', onClick: () => { void loadThread(activeConversation.id); } }}
                    />
                  </div>
                ) : messages.length === 0 ? (
                  <EmptyState
                    title="No messages yet"
                    description="This conversation has no messages."
                  />
                ) : (
                  messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Woofers Phase 2 — a reply on a text thread goes to the client as
                  a text. The box appears only when the server says one can go
                  out; otherwise staff see why (opted out, texting off, …) or,
                  on a server that predates the check, the Phase 1 note. */}
              {activeConversation.channel === 'sms' && smsReply?.canReply !== true ? (
                <p
                  className="ferro-muted"
                  data-testid={smsReply ? 'conversation-sms-reply-blocked' : 'conversation-sms-reply-note'}
                  style={{ margin: 0, padding: 12, borderTop: '1px solid var(--ferro-border)' }}
                >
                  {smsReply && !smsReply.canReply ? (
                    smsReply.message
                  ) : (
                    <>
                      This client texted your business number
                      {activeConversation.phoneNumber ? ` from ${activeConversation.phoneNumber}` : ''}. Replying by text from
                      here isn&apos;t available yet; call or text them from your phone.
                    </>
                  )}
                </p>
              ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 10,
                  padding: 12,
                  borderTop: '1px solid var(--ferro-border)',
                }}
              >
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    activeConversation.status === 'closed'
                      ? 'This conversation is closed'
                      : activeConversation.channel === 'sms'
                        ? `Text ${activeConversation.phoneNumber ?? 'the client'}…`
                        : 'Type a reply…'
                  }
                  rows={1}
                  maxLength={MAX_MESSAGE_LENGTH}
                  disabled={sending || activeConversation.status === 'closed'}
                  aria-label={activeConversation.channel === 'sms' ? 'Reply by text' : 'Reply to customer'}
                  style={{
                    flex: 1,
                    borderRadius: 'var(--ferro-radius)',
                    border: '1px solid var(--ferro-border)',
                    background: '#fff',
                    padding: '10px 14px',
                    fontSize: '16px',
                    resize: 'none',
                    minHeight: 44,
                    maxHeight: 120,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                  onInput={(e) => {
                    const t = e.target as HTMLTextAreaElement;
                    t.style.height = 'auto';
                    t.style.height = `${Math.min(t.scrollHeight, 120)}px`;
                  }}
                />
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  disabled={!draft.trim() || sending || activeConversation.status === 'closed'}
                  className={`ferro-btn${draft.trim() && !sending ? ' ferro-btn--primary' : ''}`}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-label="Send reply"
                >
                  {sending ? (
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        border: '2px solid currentColor',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'ferro-spin 0.7s linear infinite',
                        display: 'inline-block',
                      }}
                      aria-hidden="true"
                    />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  )}
                </button>
              </div>
              )}
            </>
          )}
        </div>
      </div>

    </section>
  );
}
